import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Checkbox,
  Fab,
} from '@mui/material'
import { useEffect, useMemo, useRef, useState } from 'react'
import './LineItemReview.css'
import { editingLineItemType, lineItemType, shipToType } from '../types'
import { productTypes } from '../lists'
import { Add, ExpandLess, ExpandMore, MoreVert, RefreshOutlined, SaveOutlined } from '@mui/icons-material'
import { CurrencyInput, DatePicker, NumberField } from '../helper'
import { fieldLengths } from '../fieldLengths'
import { useClassList } from '../hooks'
import ActionBar from '../Misc/ActionBar'
import {
  addBlankLineAfter,
  bulkApplyShippingAddress,
  bulkEdit,
  updatePricing,
  createBlankEditingLine,
  deleteKit,
  deleteLineItem,
  deleteSelectedLines,
  duplicateLineItem,
  lineKey,
  unkitLines,
  flattenLineItems,
  stripEditingFields,
  unflattenLineItems,
} from '../EditLines/EditLinesHelper'
import dayjs from 'dayjs'
import axios from 'axios'

const COLUMN_WIDTHS = {
  Number: '125px',
  More: '72px',
  Supplier: '250px',
  DisplayPartNumber: '175px',
  Manufacturer: '250px',
  MfrPartNumber: '175px',
  SupplierPartNumber: '250px',
  Description: '250px',
  ShipToAddress: '250px',
  Qty: '150px',
  UnitCost: '150px',
  ExtCost: '150px',
  UnitPrice: '150px',
  ExtPrice: '150px',
  TAACompliant: '250px',
  ProductCategory: '250px',
  ContractCatalog: '250px',
  CountryOfOrigin: '250px',
  PoPStart: '250px',
  PoPEnd: '250px',
  ServiceTag: '250px',
  PurchaseVehicle: '250px',
}

export default function LineItemReview({
  lineItems,
  submitLoading,
  shippingAddresses,
  isCorrection = false,
  headerPurchaseVehicle,
  setLineItems,
  setView,
  submitOrder,
  cqBackup,
  setCqBackup,
  getContraqerLineItems,
  lineItemsLoading,
  setLineItemsLoading,
}: {
  lineItems: lineItemType[]
  submitLoading: boolean
  shippingAddresses: (shipToType & { default: boolean; id: number })[]
  isCorrection?: boolean
  headerPurchaseVehicle: string
  setLineItems: React.Dispatch<React.SetStateAction<lineItemType[]>>
  setView: (view: number) => void
  submitOrder: () => void
  cqBackup?: Date | null
  setCqBackup: React.Dispatch<React.SetStateAction<Date | null>>
  getContraqerLineItems: () => void
  lineItemsLoading: 'loading' | 'error' | ''
  setLineItemsLoading: React.Dispatch<React.SetStateAction<'loading' | 'error' | ''>>
}) {
  const classList = useClassList()

  const flatLineItems = useMemo(() => flattenLineItems(lineItems), [lineItems])

  const lastCheckedIndexRef = useRef<number | null>(null)
  const skipSyncFromPropsRef = useRef(false)

  const [editingLineItems, setEditingLineItems] = useState<editingLineItemType[]>(() =>
    flatLineItems.map(item => ({ ...item, editing: false, addressId: null as number | null, selected: false })),
  )
  const [kitCollapsed, setKitCollapsed] = useState<string[]>([])
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | { lineItem: editingLineItemType; anchor: HTMLElement }>(null)
  const [kitHeaderAnchorEl, setKitHeaderAnchorEl] = useState<null | {
    kit: string
    anchor: HTMLElement
    lineKey: string
  }>(null)
  const [deleteConfirm, setDeleteConfirm] = useState<
    null | { type: 'line'; lineItem: editingLineItemType } | { type: 'kit'; kit: string }
  >(null)

  const persistToParent = (next: editingLineItemType[]) => {
    skipSyncFromPropsRef.current = true
    setLineItems(unflattenLineItems(stripEditingFields(next)))
  }

  useEffect(() => {
    if (skipSyncFromPropsRef.current) {
      skipSyncFromPropsRef.current = false
      return
    }
    setEditingLineItems(flatLineItems.map(item => ({ ...item, editing: false, addressId: null, selected: false })))
  }, [flatLineItems])

  const defaultAddress = useMemo(() => shippingAddresses.find(address => address.default), [shippingAddresses])

  const handleDuplicateLineItem = (lineItem: editingLineItemType) => {
    setEditingLineItems(prev => {
      const next = duplicateLineItem(prev, lineItem)
      persistToParent(next)
      return next
    })
    setMenuAnchorEl(null)
  }

  const handleAddLineAfter = (lineItem: editingLineItemType) => {
    setEditingLineItems(prev => {
      const newLine = createBlankEditingLine(lineItem.Kit ?? null, 0, null, null, null, null, null, null)
      const next = addBlankLineAfter(prev, lineItem, newLine)
      persistToParent(next)
      return next
    })
    setMenuAnchorEl(null)
  }

  const handleDeleteLineItem = (lineItem: editingLineItemType) => {
    setEditingLineItems(prev => {
      const next = deleteLineItem(prev, lineItem)
      persistToParent(next)
      return next
    })
    setMenuAnchorEl(null)
  }

  const handleDeleteKit = (kit: string) => {
    setEditingLineItems(prev => {
      const next = deleteKit(prev, kit)
      persistToParent(next)
      return next
    })
    setKitHeaderAnchorEl(null)
  }

  const handleDeleteSelected = () => {
    setEditingLineItems(prev => {
      const next = deleteSelectedLines(prev)
      persistToParent(next)
      return next
    })
  }

  const handleBulkEdit = (
    classVal: string,
    productCategoryVal: string,
    vendorVal: string,
    popStartDate: Date | null,
    popEndDate: Date | null,
  ) => {
    setEditingLineItems(prev => {
      const next = bulkEdit(prev, classVal, productCategoryVal, vendorVal, popStartDate, popEndDate)
      persistToParent(next)
      return next
    })
  }

  const handleBulkEditShippingAddress = (addressId: number) => {
    const address = shippingAddresses.find(a => a.id === addressId)
    if (!address) return
    setEditingLineItems(prev => {
      const next = bulkApplyShippingAddress(prev, {
        id: address.id,
        ShipToAttention: address.ShipToAttention,
        ShipToCompany: address.ShipToCompany,
        ShipToAddress1: address.ShipToAddress1,
        ShipToAddress2: address.ShipToAddress2,
        ShipToAddressCity: address.ShipToAddressCity,
        ShipToAddressState: address.ShipToAddressState,
        ShipToAddressZip: address.ShipToAddressZip,
        ShipToAddressCountry: address.ShipToAddressCountry,
        ReceiverContactName: address.ReceiverContactName,
        ReceiverContactEmail: address.ReceiverContactEmail,
        ReceiverContactPhone: address.ReceiverContactPhone,
      })
      persistToParent(next)
      return next
    })
  }

  const handleUnkitLines = (kit: string) => {
    setKitHeaderAnchorEl(null)
    setEditingLineItems(prev => {
      const next = unkitLines(prev, kit)
      persistToParent(next)
      return next
    })
    setKitCollapsed(prev => prev.filter(k => k !== kit))
  }

  const getMatchingAddress = (lineItem: lineItemType) => {
    return shippingAddresses.find(
      address =>
        address.ShipToAttention === lineItem.ShipToAttention &&
        address.ShipToCompany === lineItem.ShipToCompany &&
        address.ShipToAddress1 === lineItem.ShipToAddress1 &&
        address.ShipToAddress2 === lineItem.ShipToAddress2 &&
        address.ShipToAddressCity === lineItem.ShipToAddressCity &&
        address.ShipToAddressState === lineItem.ShipToAddressState &&
        address.ShipToAddressZip === lineItem.ShipToAddressZip &&
        address.ShipToAddressCountry === lineItem.ShipToAddressCountry &&
        address.ReceiverContactName === lineItem.ReceiverContactName &&
        address.ReceiverContactEmail === lineItem.ReceiverContactEmail &&
        address.ReceiverContactPhone === lineItem.ReceiverContactPhone,
    )
  }

  useEffect(() => {
    window.scrollTo(0, 0)

    if (isCorrection) {
      for (const lineItem of lineItems) {
        const matchingAddress = getMatchingAddress(lineItem)
        if (!matchingAddress) {
          setLineItems(prev =>
            prev.map(item =>
              lineKey(item) === lineKey(lineItem)
                ? {
                    ...item,
                    ShipToAttention: null,
                    ShipToCompany: null,
                    ShipToAddress1: null,
                    ShipToAddress2: null,
                    ShipToAddressCity: null,
                    ShipToAddressState: null,
                    ShipToAddressZip: null,
                    ShipToAddressCountry: null,
                    ReceiverContactName: null,
                    ReceiverContactEmail: null,
                    ReceiverContactPhone: null,
                  }
                : item,
            ),
          )
          setEditingLineItems(prev =>
            prev.map(item =>
              lineKey(item) === lineKey(lineItem)
                ? {
                    ...item,
                    ShipToAttention: null,
                    ShipToCompany: null,
                    ShipToAddress1: null,
                    ShipToAddress2: null,
                    ShipToAddressCity: null,
                    ShipToAddressState: null,
                    ShipToAddressZip: null,
                    ShipToAddressCountry: null,
                    ReceiverContactName: null,
                    ReceiverContactEmail: null,
                    ReceiverContactPhone: null,
                  }
                : item,
            ),
          )
        }
      }
    }
  }, [])

  return (
    <div className="line-item-review">
      <div className="line-item-review-buttons">
        <div className="line-item-review-buttons-left">
          <Button variant="contained" onClick={() => setView(1)}>
            Previous
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              setLineItemsLoading('loading')

              Promise.all([getContraqerLineItems(), axios.get('/contraqer/lastBackup')])
                .then(([_, res]) => {
                  setCqBackup(new Date(res.data.ContraqerBackupTimestamp))
                  setLineItemsLoading('')
                })
                .catch(err => {
                  console.error(err)
                  setLineItemsLoading('error')
                })
            }}
            loading={lineItemsLoading === 'loading'}
          >
            <RefreshOutlined />
            Update Lines
          </Button>
          <Typography variant="body2" color="text.secondary">
            {cqBackup ? `Last CQ Backup: ${cqBackup.toLocaleString()}` : 'No CQ Backup available'}
          </Typography>
        </div>
        <div className="line-item-review-buttons-right">
          <Button
            variant="contained"
            onClick={submitOrder}
            loading={submitLoading}
            disabled={editingLineItems.length === 0}
          >
            Create Order
          </Button>
        </div>
      </div>
      <TableContainer className="line-item-review-table">
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell width={COLUMN_WIDTHS.Number} className="line-item-review-number-column">
                <Checkbox
                  checked={editingLineItems.length > 0 && editingLineItems.every(item => item.selected)}
                  indeterminate={
                    editingLineItems.some(item => item.selected) && !editingLineItems.every(item => item.selected)
                  }
                  onChange={e =>
                    setEditingLineItems(prev => prev.map(item => ({ ...item, selected: e.target.checked })))
                  }
                />
                #
              </TableCell>
              <TableCell
                width={COLUMN_WIDTHS.More}
                className="line-item-review-more-column"
                sx={{ left: COLUMN_WIDTHS.Number }}
              />
              <TableCell
                width={COLUMN_WIDTHS.Supplier}
                className="line-item-review-supplier-column"
                sx={{ left: `calc(${COLUMN_WIDTHS.Number} + ${COLUMN_WIDTHS.More})` }}
              >
                Supplier
              </TableCell>
              <TableCell
                width={COLUMN_WIDTHS.DisplayPartNumber}
                sx={{ left: `calc(${COLUMN_WIDTHS.Number} + ${COLUMN_WIDTHS.More} + ${COLUMN_WIDTHS.Supplier})` }}
                className="line-item-review-display-part-number-column"
              >
                Display Part #
              </TableCell>
              <TableCell width={COLUMN_WIDTHS.Manufacturer}>Manufacturer</TableCell>
              <TableCell width={COLUMN_WIDTHS.MfrPartNumber}>Mfr Part #</TableCell>

              <TableCell width={COLUMN_WIDTHS.SupplierPartNumber}>Supplier Part #</TableCell>
              <TableCell width={COLUMN_WIDTHS.Description}>Description</TableCell>
              <TableCell width={COLUMN_WIDTHS.ShipToAddress}>Ship to Address</TableCell>
              <TableCell width={COLUMN_WIDTHS.Qty}>Qty</TableCell>
              <TableCell width={COLUMN_WIDTHS.UnitCost}>Unit Cost</TableCell>
              <TableCell width={COLUMN_WIDTHS.ExtCost}>Ext Cost</TableCell>
              <TableCell width={COLUMN_WIDTHS.UnitPrice}>Unit Price</TableCell>
              <TableCell width={COLUMN_WIDTHS.ExtPrice}>Ext Price</TableCell>
              <TableCell width={COLUMN_WIDTHS.TAACompliant}>TAA Compliant</TableCell>
              <TableCell width={COLUMN_WIDTHS.ProductCategory}>Product Category</TableCell>
              <TableCell width={COLUMN_WIDTHS.ContractCatalog}>Contract Number</TableCell>
              <TableCell width={COLUMN_WIDTHS.PoPStart}>PoP Start Date</TableCell>
              <TableCell width={COLUMN_WIDTHS.PoPEnd}>PoP End Date</TableCell>
              {/*
              <TableCell width={COLUMN_WIDTHS.CountryOfOrigin}>Country of Origin</TableCell>
              <TableCell width={COLUMN_WIDTHS.ServiceTag}>Service Tag</TableCell>
              */}
            </TableRow>
          </TableHead>
          <TableBody>
            {editingLineItems.map((lineItem, rowIndex) => (
              <TableRow
                key={`${rowIndex}-${lineKey(lineItem)}`}
                className={
                  lineItem.Number === -1
                    ? 'kit-row'
                    : lineItem.Number ===
                      Math.max(
                        ...editingLineItems.filter(item => item.Kit === lineItem.Kit).map(item => item.Number ?? 0),
                      )
                    ? 'last-bundle-child'
                    : 'line-item-review-table-row'
                }
                sx={{
                  display: lineItem.Number !== -1 && kitCollapsed.includes(lineItem.Kit ?? '') ? 'none' : 'table-row',
                }}
              >
                {lineItem.Number !== -1 ? (
                  <TableCell className="line-item-review-number-column">
                    <div className="line-item-review-number-cell-inner">
                      <Checkbox
                        checked={lineItem.selected}
                        onChange={e => {
                          const checked = e.target.checked
                          if ((e.nativeEvent as MouseEvent).shiftKey && lastCheckedIndexRef.current !== null) {
                            const from = Math.min(lastCheckedIndexRef.current, rowIndex)
                            const to = Math.max(lastCheckedIndexRef.current, rowIndex)
                            setEditingLineItems(prev =>
                              prev.map((item, i) =>
                                i >= from && i <= to && item.Number !== -1 ? { ...item, selected: checked } : item,
                              ),
                            )
                          } else {
                            setEditingLineItems(prev =>
                              prev.map(item =>
                                lineKey(item) === lineKey(lineItem) ? { ...item, selected: checked } : item,
                              ),
                            )
                          }
                          lastCheckedIndexRef.current = rowIndex
                        }}
                      />
                      <span className="line-item-review-number-text">
                        {lineItem.Number}
                        {lineItem.Bundle != null && lineItem.Bundle && (
                          <Chip label="Bundle" color="primary" variant="filled" size="small" />
                        )}
                      </span>
                    </div>
                  </TableCell>
                ) : (
                  <TableCell className="line-item-review-number-column">
                    <Checkbox
                      checked={editingLineItems
                        .filter(item => item.Kit === lineItem.Kit && item.Number !== -1)
                        .every(item => item.selected)}
                      indeterminate={
                        editingLineItems
                          .filter(item => item.Kit === lineItem.Kit && item.Number !== -1)
                          .some(item => item.selected) &&
                        !editingLineItems
                          .filter(item => item.Kit === lineItem.Kit && item.Number !== -1)
                          .every(item => item.selected)
                      }
                      onChange={e =>
                        setEditingLineItems(prev =>
                          prev.map(item =>
                            item.Kit === lineItem.Kit && item.Number !== -1
                              ? { ...item, selected: e.target.checked }
                              : item,
                          ),
                        )
                      }
                    />
                    <IconButton
                      onClick={() =>
                        setKitCollapsed(prev =>
                          prev.includes(lineItem.Kit ?? '')
                            ? prev.filter(kit => kit !== lineItem.Kit)
                            : [...prev, lineItem.Kit ?? ''],
                        )
                      }
                    >
                      {kitCollapsed.includes(lineItem.Kit ?? '') ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                    <Chip label="Kit" color="primary" variant="filled" size="small" />
                  </TableCell>
                )}
                <TableCell className="line-item-review-more-column" sx={{ left: COLUMN_WIDTHS.Number }}>
                  {lineItem.editing ? (
                    <IconButton
                      onClick={() => {
                        const { editing: _e, addressId: _a, selected: _s, ...rest } = lineItem
                        const toSave = editingLineItems.map(item => {
                          const { editing: _e2, addressId: _a2, selected: _s2, ...r } = item
                          return lineKey(item) === lineKey(lineItem) ? rest : r
                        })
                        setLineItems(unflattenLineItems(toSave))
                        skipSyncFromPropsRef.current = true
                        setEditingLineItems(prev =>
                          prev.map(item => (lineKey(item) === lineKey(lineItem) ? { ...item, editing: false } : item)),
                        )
                      }}
                    >
                      <SaveOutlined />
                    </IconButton>
                  ) : lineItem.Number === -1 ? (
                    <IconButton
                      onClick={e =>
                        setKitHeaderAnchorEl({
                          kit: lineItem.Kit ?? '',
                          anchor: e.currentTarget,
                          lineKey: lineKey(lineItem),
                        })
                      }
                    >
                      <MoreVert />
                    </IconButton>
                  ) : (
                    <IconButton onClick={e => setMenuAnchorEl({ lineItem, anchor: e.currentTarget })}>
                      <MoreVert />
                    </IconButton>
                  )}
                </TableCell>
                <TableCell
                  className="line-item-review-supplier-column"
                  sx={{ left: `calc(${COLUMN_WIDTHS.Number} + ${COLUMN_WIDTHS.More})` }}
                >
                  {lineItem.editing ? (
                    <TextField
                      size="small"
                      label="Supplier"
                      value={lineItem.Supplier}
                      onChange={e =>
                        setEditingLineItems(prev =>
                          prev.map(item =>
                            lineKey(item) === lineKey(lineItem) ? { ...item, Supplier: e.target.value } : item,
                          ),
                        )
                      }
                      slotProps={{ htmlInput: { maxLength: fieldLengths.lineItems.supplier } }}
                    />
                  ) : (
                    lineItem.Supplier ?? '-'
                  )}
                </TableCell>
                <TableCell
                  className="line-item-review-display-part-number-column"
                  sx={{ left: `calc(${COLUMN_WIDTHS.Number} + ${COLUMN_WIDTHS.More} + ${COLUMN_WIDTHS.Supplier})` }}
                >
                  {lineItem.editing ? (
                    <TextField
                      size="small"
                      label="Display Part #"
                      value={lineItem.DisplayPartNumber}
                      onChange={e =>
                        setEditingLineItems(prev =>
                          prev.map(item =>
                            lineKey(item) === lineKey(lineItem) ? { ...item, DisplayPartNumber: e.target.value } : item,
                          ),
                        )
                      }
                      slotProps={{ htmlInput: { maxLength: fieldLengths.lineItems.displayPartNumber } }}
                    />
                  ) : (
                    lineItem.DisplayPartNumber ?? '-'
                  )}
                </TableCell>
                <TableCell>
                  {lineItem.editing ? (
                    <TextField
                      size="small"
                      label="Manufacturer"
                      value={lineItem.Manufacturer}
                      onChange={e =>
                        setEditingLineItems(prev =>
                          prev.map(item =>
                            lineKey(item) === lineKey(lineItem) ? { ...item, Manufacturer: e.target.value } : item,
                          ),
                        )
                      }
                      slotProps={{ htmlInput: { maxLength: fieldLengths.lineItems.manufacturer } }}
                    />
                  ) : (
                    lineItem.Manufacturer ?? '-'
                  )}
                </TableCell>
                <TableCell>
                  {lineItem.editing ? (
                    <TextField
                      size="small"
                      label="Mfr Part #"
                      value={lineItem.MfrPartNum}
                      onChange={e =>
                        setEditingLineItems(prev =>
                          prev.map(item =>
                            lineKey(item) === lineKey(lineItem) ? { ...item, MfrPartNum: e.target.value } : item,
                          ),
                        )
                      }
                      slotProps={{ htmlInput: { maxLength: fieldLengths.lineItems.mfrPartNum } }}
                    />
                  ) : (
                    lineItem.MfrPartNum ?? '-'
                  )}
                </TableCell>
                <TableCell>
                  {lineItem.editing ? (
                    <TextField
                      size="small"
                      label="Supplier Part Number"
                      value={lineItem.SupplierPartNum}
                      onChange={e =>
                        setEditingLineItems(prev =>
                          prev.map(item =>
                            lineKey(item) === lineKey(lineItem) ? { ...item, SupplierPartNum: e.target.value } : item,
                          ),
                        )
                      }
                      slotProps={{ htmlInput: { maxLength: fieldLengths.lineItems.supplierPartNumber } }}
                    />
                  ) : (
                    lineItem.SupplierPartNum ?? '-'
                  )}
                </TableCell>
                <TableCell>
                  {lineItem.editing ? (
                    <TextField
                      size="small"
                      label="Product Description"
                      value={lineItem.ProductDescription}
                      onChange={e =>
                        setEditingLineItems(prev =>
                          prev.map(item =>
                            lineKey(item) === lineKey(lineItem)
                              ? { ...item, ProductDescription: e.target.value }
                              : item,
                          ),
                        )
                      }
                      slotProps={{ htmlInput: { maxLength: fieldLengths.lineItems.productDescription } }}
                    />
                  ) : (
                    lineItem.ProductDescription ?? '-'
                  )}
                </TableCell>
                <TableCell>
                  {lineItem.editing ? (
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Ship to Address"
                      value={lineItem.addressId ?? defaultAddress?.id ?? null}
                      onChange={e => {
                        const address = shippingAddresses.find(address => address.id === parseInt(e.target.value))
                        if (address) {
                          setEditingLineItems(prev =>
                            prev.map(item =>
                              lineKey(item) === lineKey(lineItem)
                                ? {
                                    ...item,
                                    ShipToAttention: address.ShipToAttention,
                                    ShipToCompany: address.ShipToCompany,
                                    ShipToAddress1: address.ShipToAddress1,
                                    ShipToAddress2: address.ShipToAddress2,
                                    ShipToAddressCity: address.ShipToAddressCity,
                                    ShipToAddressState: address.ShipToAddressState,
                                    ShipToAddressZip: address.ShipToAddressZip,
                                    ShipToAddressCountry: address.ShipToAddressCountry,
                                    ReceiverContactName: address.ReceiverContactName,
                                    ReceiverContactEmail: address.ReceiverContactEmail,
                                    ReceiverContactPhone: address.ReceiverContactPhone,
                                    addressId: address.id,
                                  }
                                : item,
                            ),
                          )
                        }
                      }}
                    >
                      {shippingAddresses.map(address => (
                        <MenuItem key={address.id} value={address.id}>
                          {address.ShipToAttention ?? ''} {address.ShipToCompany ?? ''} {address.ShipToAddress1 ?? ''}{' '}
                          {address.ShipToAddress2 ?? ''} {address.ShipToAddressCity ?? ''}{' '}
                          {address.ShipToAddressState ?? ''} {address.ShipToAddressZip ?? ''}{' '}
                          {address.ShipToAddressCountry ?? ''}
                        </MenuItem>
                      ))}
                    </TextField>
                  ) : lineItem.ShipToAddress1 ? (
                    `${lineItem.ShipToAttention ?? ''} ${lineItem.ShipToCompany ?? ''} ${lineItem.ShipToAddress1} ${
                      lineItem.ShipToAddress2 ?? ''
                    } ${lineItem.ShipToAddressCity ?? ''} ${lineItem.ShipToAddressState ?? ''} ${
                      lineItem.ShipToAddressZip ?? ''
                    } ${lineItem.ShipToAddressCountry ?? ''}`
                  ) : (
                    `${defaultAddress?.ShipToAttention ?? ''} ${defaultAddress?.ShipToCompany ?? ''} ${
                      defaultAddress?.ShipToAddress1 ?? ''
                    } ${defaultAddress?.ShipToAddress2 ?? ''} ${defaultAddress?.ShipToAddressCity ?? ''} ${
                      defaultAddress?.ShipToAddressState ?? ''
                    } ${defaultAddress?.ShipToAddressZip ?? ''} ${defaultAddress?.ShipToAddressCountry ?? ''}`
                  )}
                </TableCell>
                <TableCell>
                  {lineItem.editing ? (
                    <NumberField
                      size="small"
                      label="Qty"
                      value={lineItem.Quantity}
                      onChange={value =>
                        setEditingLineItems(prev =>
                          prev.map(item =>
                            lineKey(item) === lineKey(lineItem)
                              ? { ...item, ...updatePricing(item, 'Quantity', value) }
                              : item,
                          ),
                        )
                      }
                    />
                  ) : (
                    lineItem.Quantity ?? '-'
                  )}
                </TableCell>
                <TableCell>
                  {lineItem.editing ? (
                    <CurrencyInput
                      size="small"
                      label="Unit Cost"
                      value={lineItem.UnitCost ?? null}
                      onChange={value =>
                        setEditingLineItems(prev =>
                          prev.map(item =>
                            lineKey(item) === lineKey(lineItem)
                              ? { ...item, ...updatePricing(item, 'UnitCost', value) }
                              : item,
                          ),
                        )
                      }
                    />
                  ) : (
                    lineItem.UnitCost?.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) ?? '-'
                  )}
                </TableCell>
                <TableCell>
                  {lineItem.editing ? (
                    <CurrencyInput
                      size="small"
                      label="Ext Cost"
                      value={lineItem.ExtCost ?? null}
                      onChange={value =>
                        setEditingLineItems(prev =>
                          prev.map(item =>
                            lineKey(item) === lineKey(lineItem)
                              ? { ...item, ...updatePricing(item, 'ExtCost', value) }
                              : item,
                          ),
                        )
                      }
                    />
                  ) : (
                    lineItem.ExtCost?.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) ?? '-'
                  )}
                </TableCell>
                <TableCell>
                  {lineItem.editing ? (
                    <CurrencyInput
                      size="small"
                      label="Unit Price"
                      value={lineItem.UnitPrice ?? null}
                      onChange={value =>
                        setEditingLineItems(prev =>
                          prev.map(item =>
                            lineKey(item) === lineKey(lineItem)
                              ? { ...item, ...updatePricing(item, 'UnitPrice', value) }
                              : item,
                          ),
                        )
                      }
                    />
                  ) : (
                    lineItem.UnitPrice?.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) ?? '-'
                  )}
                </TableCell>
                <TableCell>
                  {lineItem.editing ? (
                    <CurrencyInput
                      size="small"
                      label="Ext Price"
                      value={lineItem.ExtPrice ?? null}
                      onChange={value =>
                        setEditingLineItems(prev =>
                          prev.map(item =>
                            lineKey(item) === lineKey(lineItem)
                              ? { ...item, ...updatePricing(item, 'ExtPrice', value) }
                              : item,
                          ),
                        )
                      }
                    />
                  ) : (
                    lineItem.ExtPrice?.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) ?? '-'
                  )}
                </TableCell>
                <TableCell>
                  {lineItem.editing ? (
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="TAA Compliant"
                      value={lineItem.TaaCompliant ? 'Yes' : lineItem.TaaCompliant === false ? 'No' : 'N/A'}
                      onChange={e =>
                        setEditingLineItems(prev =>
                          prev.map(item =>
                            lineKey(item) === lineKey(lineItem)
                              ? { ...item, TaaCompliant: e.target.value === 'N/A' ? null : e.target.value === 'Yes' }
                              : item,
                          ),
                        )
                      }
                    >
                      <MenuItem value="N/A">Not Applicable</MenuItem>
                      <MenuItem value="Yes">Yes</MenuItem>
                      <MenuItem value="No">No</MenuItem>
                    </TextField>
                  ) : lineItem.TaaCompliant ? (
                    'Yes'
                  ) : lineItem.TaaCompliant === false ? (
                    'No'
                  ) : (
                    'Not Applicable'
                  )}
                </TableCell>
                <TableCell>
                  {lineItem.editing ? (
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Product Category"
                      value={lineItem.ProductCategory}
                      onChange={e =>
                        setEditingLineItems(prev =>
                          prev.map(item =>
                            lineKey(item) === lineKey(lineItem) ? { ...item, ProductCategory: e.target.value } : item,
                          ),
                        )
                      }
                    >
                      {productTypes.map(type => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      ))}
                    </TextField>
                  ) : (
                    lineItem.ProductCategory ?? '-'
                  )}
                </TableCell>
                <TableCell>
                  {lineItem.editing ? (
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Contract Number"
                      value={lineItem.Class}
                      onChange={e =>
                        setEditingLineItems(prev =>
                          prev.map(item =>
                            lineKey(item) === lineKey(lineItem) ? { ...item, Class: e.target.value } : item,
                          ),
                        )
                      }
                    >
                      {classList
                        .find(cls => cls.name === headerPurchaseVehicle)
                        ?.children?.map(cls => (
                          <MenuItem key={cls.id} value={cls.name}>
                            {cls.name}
                          </MenuItem>
                        ))}
                    </TextField>
                  ) : (
                    lineItem.Class ?? '-'
                  )}
                </TableCell>
                <TableCell>
                  {lineItem.editing ? (
                    <DatePicker
                      size="small"
                      label="PoP Start Date"
                      value={lineItem.PopStartDate ? dayjs.utc(lineItem.PopStartDate) : null}
                      onChange={value =>
                        setEditingLineItems(prev =>
                          prev.map(item =>
                            lineKey(item) === lineKey(lineItem)
                              ? { ...item, PopStartDate: value?.toDate() ?? null }
                              : item,
                          ),
                        )
                      }
                    />
                  ) : lineItem.PopStartDate ? (
                    dayjs.utc(lineItem.PopStartDate).format('MM/DD/YYYY')
                  ) : (
                    '-'
                  )}
                </TableCell>
                <TableCell>
                  {lineItem.editing ? (
                    <DatePicker
                      size="small"
                      label="PoP End Date"
                      value={lineItem.PopEndDate ? dayjs.utc(lineItem.PopEndDate) : null}
                      onChange={value =>
                        setEditingLineItems(prev =>
                          prev.map(item =>
                            lineKey(item) === lineKey(lineItem)
                              ? { ...item, PopEndDate: value?.toDate() ?? null }
                              : item,
                          ),
                        )
                      }
                    />
                  ) : lineItem.PopEndDate ? (
                    dayjs.utc(lineItem.PopEndDate).format('MM/DD/YYYY')
                  ) : (
                    '-'
                  )}
                </TableCell>
                {/*
                <TableCell>
                  {lineItem.editing ? (
                    <TextField
                      size="small"
                      label="Country of Origin"
                      value={lineItem.CountryOfOrigin}
                      onChange={e =>
                        setEditingLineItems(prev =>
                          prev.map(item =>
                            lineKey(item) === lineKey(lineItem) ? { ...item, CountryOfOrigin: e.target.value } : item,
                          ),
                        )
                      }
                    />
                  ) : (
                    lineItem.CountryOfOrigin ?? '-'
                  )}
                </TableCell>
                <TableCell>
                  {lineItem.editing ? (
                    <TextField
                      size="small"
                      label="Service Tag"
                      value={lineItem.ServiceTag}
                      onChange={e =>
                        setEditingLineItems(prev =>
                          prev.map(item =>
                            lineKey(item) === lineKey(lineItem) ? { ...item, ServiceTag: e.target.value } : item,
                          ),
                        )
                      }
                    />
                  ) : (
                    lineItem.ServiceTag ?? '-'
                  )}
                </TableCell>
                */}
              </TableRow>
            ))}
          </TableBody>
          <div className="add-new-line-fab-container">
            <Fab
              color="primary"
              variant="extended"
              size="large"
              onClick={() =>
                setEditingLineItems(prev => [
                  ...prev,
                  createBlankEditingLine(
                    null,
                    editingLineItems.filter(item => item.Number !== null && item.Number > 0).length + 1,
                    null,
                    null,
                    null,
                    null,
                    null,
                    null,
                  ),
                ])
              }
            >
              Add New Line <Add />
            </Fab>
          </div>
        </Table>
      </TableContainer>

      <Menu open={Boolean(menuAnchorEl)} onClose={() => setMenuAnchorEl(null)} anchorEl={menuAnchorEl?.anchor}>
        <MenuItem onClick={() => menuAnchorEl && handleAddLineAfter(menuAnchorEl.lineItem)}>Add line</MenuItem>
        <MenuItem
          onClick={() => {
            if (menuAnchorEl) {
              setEditingLineItems(prev =>
                prev.map(item =>
                  item.Number === menuAnchorEl.lineItem.Number && item.Kit === menuAnchorEl.lineItem.Kit
                    ? { ...item, editing: true }
                    : item,
                ),
              )
              setMenuAnchorEl(null)
            }
          }}
        >
          Edit
        </MenuItem>
        <MenuItem onClick={() => menuAnchorEl && handleDuplicateLineItem(menuAnchorEl.lineItem)}>Duplicate</MenuItem>
        <MenuItem
          onClick={() => {
            if (menuAnchorEl) {
              setDeleteConfirm({ type: 'line', lineItem: menuAnchorEl.lineItem })
              setMenuAnchorEl(null)
            }
          }}
        >
          Delete
        </MenuItem>
      </Menu>

      <Menu
        open={Boolean(kitHeaderAnchorEl)}
        onClose={() => setKitHeaderAnchorEl(null)}
        anchorEl={kitHeaderAnchorEl?.anchor}
      >
        <MenuItem
          onClick={() => {
            if (kitHeaderAnchorEl) {
              setEditingLineItems(prev =>
                prev.map(item => (lineKey(item) === kitHeaderAnchorEl.lineKey ? { ...item, editing: true } : item)),
              )
              setKitHeaderAnchorEl(null)
            }
          }}
        >
          Edit
        </MenuItem>
        <MenuItem onClick={() => kitHeaderAnchorEl && handleUnkitLines(kitHeaderAnchorEl.kit)}>Unkit lines</MenuItem>
        <MenuItem
          onClick={() => {
            if (kitHeaderAnchorEl) {
              setDeleteConfirm({ type: 'kit', kit: kitHeaderAnchorEl.kit })
              setKitHeaderAnchorEl(null)
            }
          }}
        >
          Delete kit
        </MenuItem>
      </Menu>

      <Dialog open={Boolean(deleteConfirm)} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>{deleteConfirm?.type === 'kit' ? 'Delete kit' : 'Delete line'}</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to delete the selected {deleteConfirm?.type === 'kit' ? 'kit' : 'line items'}? This
            change applies to your order until you go back and load the line items again.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setDeleteConfirm(null)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (deleteConfirm?.type === 'line') {
                handleDeleteLineItem(deleteConfirm.lineItem)
              } else if (deleteConfirm?.type === 'kit') {
                handleDeleteKit(deleteConfirm.kit)
              }
              setDeleteConfirm(null)
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <ActionBar
        bulkActionsOnly
        headerPurchaseVehicle={headerPurchaseVehicle}
        lineItems={editingLineItems}
        setLineItems={items => setEditingLineItems(items as editingLineItemType[])}
        totalCount={editingLineItems.length}
        selectedLineItems={editingLineItems.filter(item => item.selected)}
        setSelectedLineItems={items =>
          setEditingLineItems(prev =>
            prev.map(item => (items.includes(item) ? { ...item, selected: true } : { ...item, selected: false })),
          )
        }
        onDeleteSelected={handleDeleteSelected}
        onBulkEdit={handleBulkEdit}
        onBulkEditShippingAddress={handleBulkEditShippingAddress}
        shippingAddresses={shippingAddresses}
      />
    </div>
  )
}
